import React from 'react';
import { motion } from 'framer-motion';
import { Truck, Package, MapPin, Wind, Shield } from 'lucide-react';
import fleetHeroImage from '../../assets/cvj-fleet-hero.jpg'; // Hero image
import heavyTruckImage from '../../assets/cvj-heavy-truck.jpg'; // Individual fleet images
import refrigeratedVanImage from '../../assets/cvj-refrigerated.jpg';
import flatbedTrailerImage from '../../assets/cvj-flatbed.jpg';
import curtainSideLorryImage from '../../assets/cvj-lorry.jpg';
import lowLoaderImage from '../../assets/cvj-low-loader.jpg';
import './FleetSection.css';

const FleetSection = () => {
  const fleet = [
    {
      type: 'Heavy Duty Trucks',
      description: 'High-capacity trucks for large-scale freight across the UK.',
      capacity: '40 Tonnes',
      range: '1,500 km',
      fuelEfficiency: '8 mpg',
      useCase: 'Bulk Goods & Construction Materials',
      icon: <Truck size={40} />,
      image: heavyTruckImage, // Added image
    },
    {
      type: 'Refrigerated Vans',
      description: 'Temperature-controlled vans for perishables and pharmaceuticals.',
      capacity: '5 Tonnes',
      range: '800 km',
      fuelEfficiency: '15 mpg',
      useCase: 'Food & Medical Supplies',
      icon: <Package size={40} />,
      image: refrigeratedVanImage,
    },
    {
      type: 'Flatbed Trailers',
      description: 'Versatile trailers for oversized or heavy equipment.',
      capacity: '30 Tonnes',
      range: '1,200 km',
      fuelEfficiency: '9 mpg',
      useCase: 'Machinery & Vehicles',
      icon: <MapPin size={40} />,
      image: flatbedTrailerImage,
    },
    {
      type: 'Curtain-Side Lorries',
      description: 'Flexible loading for palletized goods with weather protection.',
      capacity: '25 Tonnes',
      range: '1,300 km',
      fuelEfficiency: '10 mpg',
      useCase: 'Retail & General Cargo',
      icon: <Wind size={40} />,
      image: curtainSideLorryImage,
    },
    {
      type: 'Low Loader Trailers',
      description: 'Specialized for transporting tall or heavy machinery.',
      capacity: '50 Tonnes',
      range: '1,000 km',
      fuelEfficiency: '7 mpg',
      useCase: 'Industrial Equipment',
      icon: <Shield size={40} />,
      image: lowLoaderImage,
    },
  ];

  const cardVariants = {
    hidden: { opacity: 0, x: '-50vw' },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, delay: i * 0.2, ease: 'easeOut' },
    }),
    hover: { scale: 1.03, boxShadow: '0 10px 30px rgba(0, 221, 235, 0.4)' },
  };

  const statsVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  return (
    <section id="fleet" className="fleet-section">
      {/* Hero Section with Image */}
      <motion.div
        className="fleet-hero"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
        style={{ backgroundImage: `url(${fleetHeroImage})` }} // Inline style for hero image
      >
        <div className="hero-overlay">
          <h2 className="fleet-title">Our Fleet</h2>
        </div>
      </motion.div>

      <div className="fleet-roadway" />
      <div className="fleet-convoy">
        {fleet.map((vehicle, index) => (
          <motion.div
            key={index}
            className="fleet-card"
            custom={index}
            variants={cardVariants}
            initial="hidden"
            whileInView="visible"
            whileHover="hover"
            viewport={{ once: true }}
            style={{ backgroundImage: `url(${vehicle.image})` }} // Inline style for card image
          >
            <div className="fleet-icon">{vehicle.icon}</div>
            <div className="fleet-content">
              <h3 className="fleet-type">{vehicle.type}</h3>
              <p className="fleet-description">{vehicle.description}</p>
              <div className="fleet-specs">
                <span className="spec-item">Cap: {vehicle.capacity}</span>
                <span className="spec-item">Rng: {vehicle.range}</span>
                <span className="spec-item">Fuel: {vehicle.fuelEfficiency}</span>
                <span className="spec-item tooltip">
                  Use: {vehicle.useCase.split(' ')[0]}
                  <span className="tooltip-text">{vehicle.useCase}</span>
                </span>
              </div>
              <motion.a
                href="#contact"
                className="fleet-link"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.3 }}
              >
                Request Quote
              </motion.a>
            </div>
          </motion.div>
        ))}
      </div>
      <motion.div
        className="fleet-stats"
        initial="hidden"
        whileInView="visible"
        variants={statsVariants}
        viewport={{ once: true }}
      >
        <h3 className="stats-title">Fleet at a Glance</h3>
        <div className="stats-grid">
          <div className="stat-box">
            <span className="stat-value">{fleet.length}</span>
            <span className="stat-label">Types</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">150+</span>
            <span className="stat-label">Units</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">10K+</span>
            <span className="stat-label">Deliveries</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">99%</span>
            <span className="stat-label">On-Time</span>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default FleetSection;